/**
 * User Engagement Level Calculation
 * Based on behavioral psychology and progressive disclosure principles
 */

export type EngagementLevel = 'beginner' | 'engaged' | 'power_user';

export interface UserMetrics {
  totalWorkouts: number;
  currentStreak: number;
  weeklyWorkouts: number;
  accountAge: number; // in days
  lastWorkoutDate: string | null;
  hasCompletedFirstWorkout: boolean;
  hasSetGoals: boolean;
  hasConnectedWithCoach: boolean;
}

/**
 * Determine user engagement level based on multiple factors
 * Using a point-based system with thresholds
 */
export const getUserEngagementLevel = (metrics: UserMetrics): EngagementLevel => {
  let engagementScore = 0;

  // Account age scoring (max 20 points)
  if (metrics.accountAge >= 30) {
    engagementScore += 20;
  } else if (metrics.accountAge >= 14) {
    engagementScore += 15;
  } else if (metrics.accountAge >= 7) {
    engagementScore += 10;
  } else if (metrics.accountAge >= 3) {
    engagementScore += 5;
  }

  // Workout frequency scoring (max 30 points)
  if (metrics.totalWorkouts >= 20) {
    engagementScore += 30;
  } else if (metrics.totalWorkouts >= 10) {
    engagementScore += 20;
  } else if (metrics.totalWorkouts >= 5) {
    engagementScore += 15;
  } else if (metrics.totalWorkouts >= 1) {
    engagementScore += 10;
  }

  // Current streak scoring (max 20 points)
  if (metrics.currentStreak >= 7) {
    engagementScore += 20;
  } else if (metrics.currentStreak >= 3) {
    engagementScore += 15;
  } else if (metrics.currentStreak >= 1) {
    engagementScore += 10;
  }

  // Weekly consistency scoring (max 15 points)
  if (metrics.weeklyWorkouts >= 4) {
    engagementScore += 15;
  } else if (metrics.weeklyWorkouts >= 3) {
    engagementScore += 10;
  } else if (metrics.weeklyWorkouts >= 2) {
    engagementScore += 5;
  }

  // Behavioral indicators (max 15 points)
  if (metrics.hasCompletedFirstWorkout) engagementScore += 5;
  if (metrics.hasSetGoals) engagementScore += 5;
  if (metrics.hasConnectedWithCoach) engagementScore += 5;

  // Recent activity bonus
  if (metrics.lastWorkoutDate) {
    const lastWorkout = new Date(metrics.lastWorkoutDate);
    const daysSinceLastWorkout = Math.floor(
      (Date.now() - lastWorkout.getTime()) / (1000 * 60 * 60 * 24)
    );
    
    if (daysSinceLastWorkout <= 2) {
      engagementScore += 5; // Active in last 2 days
    }
  }

  // Determine level based on score thresholds
  if (engagementScore >= 70) {
    return 'power_user';
  } else if (engagementScore >= 35) {
    return 'engaged';
  } else {
    return 'beginner';
  }
};

/**
 * Get progression percentage to next level
 */
export const getProgressionToNextLevel = (
  metrics: UserMetrics,
  currentLevel: EngagementLevel
): number => {
  const score = calculateEngagementScore(metrics);
  
  switch (currentLevel) {
    case 'beginner':
      // Progress from 0 to 35
      return Math.min((score / 35) * 100, 100);
    case 'engaged':
      // Progress from 35 to 70
      return Math.min(((score - 35) / 35) * 100, 100);
    case 'power_user':
      // Already at max level
      return 100;
    default:
      return 0;
  }
};

/**
 * Calculate raw engagement score
 */
const calculateEngagementScore = (metrics: UserMetrics): number => {
  let score = 0;

  // Same scoring logic as getUserEngagementLevel
  // (Refactored to avoid duplication)
  
  // Account age
  if (metrics.accountAge >= 30) score += 20;
  else if (metrics.accountAge >= 14) score += 15;
  else if (metrics.accountAge >= 7) score += 10;
  else if (metrics.accountAge >= 3) score += 5;

  // Total workouts
  if (metrics.totalWorkouts >= 20) score += 30;
  else if (metrics.totalWorkouts >= 10) score += 20;
  else if (metrics.totalWorkouts >= 5) score += 15;
  else if (metrics.totalWorkouts >= 1) score += 10;

  // Streak
  if (metrics.currentStreak >= 7) score += 20;
  else if (metrics.currentStreak >= 3) score += 15;
  else if (metrics.currentStreak >= 1) score += 10;

  // Weekly workouts
  if (metrics.weeklyWorkouts >= 4) score += 15;
  else if (metrics.weeklyWorkouts >= 3) score += 10;
  else if (metrics.weeklyWorkouts >= 2) score += 5;

  // Behavioral
  if (metrics.hasCompletedFirstWorkout) score += 5;
  if (metrics.hasSetGoals) score += 5;
  if (metrics.hasConnectedWithCoach) score += 5;

  // Recent activity
  if (metrics.lastWorkoutDate) {
    const daysSince = Math.floor(
      (Date.now() - new Date(metrics.lastWorkoutDate).getTime()) / (1000 * 60 * 60 * 24)
    );
    if (daysSince <= 2) score += 5;
  }

  return score;
};

/**
 * Get motivational message based on engagement level and progress
 */
export const getMotivationalMessage = (
  level: EngagementLevel,
  metrics: UserMetrics
): string => {
  if (!metrics.hasCompletedFirstWorkout) {
    return "Ready to start? Your first workout is waiting! 💪";
  }

  switch (level) {
    case 'beginner':
      if (metrics.totalWorkouts < 3) {
        return "Great start! Keep the momentum going!";
      } else {
        return "You're building a solid foundation. Stay consistent!";
      }
    
    case 'engaged':
      if (metrics.currentStreak >= 3) {
        return `${metrics.currentStreak} day streak! You're on fire! 🔥`;
      } else {
        return "You're making excellent progress. Keep pushing!";
      }
    
    case 'power_user':
      if (metrics.currentStreak >= 7) {
        return `Unstoppable! ${metrics.currentStreak} days and counting! 🚀`;
      } else {
        return "Elite level performance. You inspire others!";
      }
    
    default:
      return "Keep up the great work!";
  }
};